/**
 * Utility functions for handling credentials in edit mode
 */

/**
 * List of credential field names that should be filtered out if empty during updates
 */
const CREDENTIAL_FIELDS = [
    'password',
    'accessKeyId',
    'secretAccessKey',
    'sessionToken',
    'username',
    'bearerToken',
    'privateKey',
    'token',
    'apiKey',
    'clientId',
] as const;

/**
 * Recursively filters out empty credential fields from a configuration object.
 * This is used in edit mode to prevent overwriting existing credentials with empty values.
 *
 * @param config - The configuration object to filter
 * @returns A new configuration object with empty credential fields removed
 */
export function filterEmptyCredentials<T extends Record<string, any>>(config: T): Partial<T> {
    if (!config || typeof config !== 'object') {
        return config;
    }

    const filtered: any = {};

    for (const [key, value] of Object.entries(config)) {
        // Skip undefined, null, or empty string values for credential fields
        if (CREDENTIAL_FIELDS.includes(key as any)) {
            if (value !== undefined && value !== null && value !== '') {
                filtered[key] = value;
            }
            // If empty, skip this field (don't include it in the update)
        } else if (value && typeof value === 'object' && !Array.isArray(value)) {
            // Recursively filter nested objects
            const nestedFiltered = filterEmptyCredentials(value);
            if (Object.keys(nestedFiltered).length > 0) {
                filtered[key] = nestedFiltered;
            }
        } else {
            // Keep non-credential fields as-is
            filtered[key] = value;
        }
    }

    return filtered;
}

/**
 * Checks if a configuration object has any non-empty credential fields
 */
export function hasCredentials(config: Record<string, any> | undefined): boolean {
    if (!config) return false;

    for (const key of Object.keys(config)) {
        if (CREDENTIAL_FIELDS.includes(key as any)) {
            const value = config[key];
            if (value !== undefined && value !== null && value !== '') {
                return true;
            }
        }
        // Check nested objects
        if (config[key] && typeof config[key] === 'object' && !Array.isArray(config[key])) {
            if (hasCredentials(config[key])) {
                return true;
            }
        }
    }

    return false;
}

/**
 * Checks if a value contains masked data (asterisks pattern)
 * API returns some sensitive fields with masking like "dsdsad***"
 */
function isMaskedValue(value: any): boolean {
    if (typeof value !== 'string') {
        return false;
    }
    // Check if the value contains asterisks, indicating it's been masked by the API
    return value.includes('***') || value.includes('****') || /\*{3,}/.test(value);
}

/**
 * Removes all credential fields from a configuration object.
 * Use this to prepare configuration data for display in edit forms.
 * This ensures credentials are never shown in the UI.
 *
 * Also removes any fields with masked values (containing asterisks) as they
 * indicate the API has hidden sensitive data.
 *
 * @param config - The configuration object to strip credentials from
 * @returns A new configuration object without any credential fields or masked values
 */
export function stripCredentials<T extends Record<string, any>>(config: T): Partial<T> {
    if (!config || typeof config !== 'object') {
        return config;
    }

    const stripped: any = {};

    for (const [key, value] of Object.entries(config)) {
        // Skip all credential fields
        if (CREDENTIAL_FIELDS.includes(key as any)) {
            // Don't include this field at all
            continue;
        }
        // Skip fields with masked values (e.g., "dsdsad***")
        else if (isMaskedValue(value)) {
            // Don't include masked fields - they're sensitive data
            continue;
        } else if (value && typeof value === 'object' && !Array.isArray(value)) {
            // Recursively strip nested objects
            const nestedStripped = stripCredentials(value);
            if (Object.keys(nestedStripped).length > 0) {
                stripped[key] = nestedStripped;
            }
        } else {
            // Keep non-credential fields
            stripped[key] = value;
        }
    }

    return stripped;
}
